import { Box, Paper, Typography } from '@mui/material';
import { useEffect } from 'react';

import { useBaermannListByProtocolo } from '@/features/analisis/baermann/hooks/useBaermann';
import { useCoprocultivoListByProtocolo } from '@/features/analisis/coprocultivo/hooks/useCoprocultivo';
import { useEctoparasitosListByProtocolo } from '@/features/analisis/ectoparasitos/hooks/useEctoparasitos';
import { useHpgListByProtocolo } from '@/features/analisis/hpg/hooks/useHpg';
import { useOpgListByProtocolo } from '@/features/analisis/opg/hooks/useOpg';
import { useSedimentacionFhListByProtocolo } from '@/features/analisis/sedimentacion-fh/hooks/useSedimentacionFh';
import {
  useInformeFinal,
  useInformeParcial,
} from '@/features/informes/hooks/useInformes';
import { useNotify } from '@/hooks/useNotify';
import { abrirInformePDF } from '@/lib/abrirInformePDF';

import { InformeFinalEmitidoCard } from './InformeFinalEmitidoCard';
import { InformeParcialCard } from './InformeParcialCard';

interface SeccionInformesProps {
  uidProtocolo: string;
  isClosed: boolean;
  canManage: boolean;
}

export function SeccionInformes({
  uidProtocolo,
  isClosed,
  canManage,
}: SeccionInformesProps): JSX.Element | null {
  const notify = useNotify();

  const informeParcialQuery = useInformeParcial(uidProtocolo);
  const informeFinalQuery = useInformeFinal(uidProtocolo);

  const hpgQuery = useHpgListByProtocolo(uidProtocolo);
  const opgQuery = useOpgListByProtocolo(uidProtocolo);
  const coprocultivoQuery = useCoprocultivoListByProtocolo(uidProtocolo);
  const baermannQuery = useBaermannListByProtocolo(uidProtocolo);
  const sedFhQuery = useSedimentacionFhListByProtocolo(uidProtocolo);
  const ectoQuery = useEctoparasitosListByProtocolo(uidProtocolo);

  const totalAnalisis =
    (hpgQuery.data?.length ?? 0) +
    (opgQuery.data?.length ?? 0) +
    (coprocultivoQuery.data?.length ?? 0) +
    (baermannQuery.data?.length ?? 0) +
    (sedFhQuery.data?.length ?? 0) +
    (ectoQuery.data?.length ?? 0);
  const hayAnalisis = totalAnalisis > 0;

  const { refetch: refetchFinal } = informeFinalQuery;
  useEffect(() => {
    if (isClosed) {
      void refetchFinal();
    }
  }, [isClosed, uidProtocolo, refetchFinal]);

  if (!canManage) return null;

  async function handleVerDescargarParcial(): Promise<void> {
    await abrirInformePDF(
      async () => {
        const result = await informeParcialQuery.refetch();
        return result.data?.presigned_url;
      },
      notify.error,
      'No se pudo generar el informe parcial. Intentá de nuevo.',
    );
  }

  async function handleVerDescargarFinal(): Promise<void> {
    await abrirInformePDF(
      async () => {
        const result = await informeFinalQuery.refetch();
        return result.data?.presigned_url;
      },
      notify.error,
      'No se pudo obtener el informe final. Intentá de nuevo.',
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Box mb={2.5}>
        <Typography variant="h6">Informes</Typography>
        <Typography variant="body2" color="text.secondary">
          {isClosed
            ? 'El protocolo está cerrado.'
            : 'Vista previa del informe parcial mientras el protocolo está en curso.'}
        </Typography>
      </Box>

      {isClosed ? (
        <InformeFinalEmitidoCard
          fechaEmision={null}
          isFetching={informeFinalQuery.isFetching}
          onVerDescargar={() => void handleVerDescargarFinal()}
        />
      ) : (
        <>
          <InformeParcialCard
            hayAnalisis={hayAnalisis}
            isFetching={informeParcialQuery.isFetching}
            onVerDescargar={() => void handleVerDescargarParcial()}
          />
          <Typography
            variant="caption"
            color="text.disabled"
            display="block"
            mt={1.5}
            fontStyle="italic"
          >
            El informe final se emite desde la sección de envíos al marcar la opción
            correspondiente.
          </Typography>
        </>
      )}
    </Paper>
  );
}
